import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { healthDataService } from '../services/health-data.service';
import { useFeatureFlags } from './useFeatureFlags';
import axios from 'axios';

/** Dummy sugar readings when backend returns no data - for UI preview */
function getDummySugarReadings() {
  const readings: Array<{ value: number; readingTime?: string; createdAt: string }> = [];
  for (let i = 0; i < 14; i++) {
    const date = new Date();
    date.setDate(date.getDate() - (13 - i));
    const value = 80 + Math.random() * 80; // 80-160 mg/dL
    readings.push({
      value: Math.round(value * 10) / 10,
      createdAt: date.toISOString(),
      readingTime: date.toISOString(),
    });
  }
  return readings;
}

/** Stable date strings for last 7 days - avoids query key churn and duplicate requests */
function get7DayRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 7);
  const startStr = start.toISOString().split('T')[0];
  const endStr = end.toISOString().split('T')[0];
  return {
    startDate: `${startStr}T00:00:00.000Z`,
    endDate: `${endStr}T23:59:59.999Z`,
    startStr,
    endStr,
    start,
    end,
  };
}

/** Don't retry on 429 - immediate retries worsen rate limiting */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (axios.isAxiosError(error) && error.response?.status === 429) {
    return false;
  }
  return failureCount < 1;
}

/**
 * Shared hook for sugar readings (last 7 days). Used by GlucoseTrends,
 * LowBloodGlucoseIndex, and MultiDayGlucoseChart to deduplicate requests.
 */
export function useSugarReadings7Days() {
  const { user } = useAuth();
  const { useDummyDataWhenEmpty } = useFeatureFlags();
  const { startDate, endDate, startStr, endStr } = get7DayRange();

  const query = useQuery({
    queryKey: ['sugar-readings', user?.id, '7d', startStr, endStr, useDummyDataWhenEmpty],
    queryFn: async () => {
      try {
        const result = await healthDataService.getSugarReadings({
          startDate,
          endDate,
          limit: 500,
        });
        const arr = Array.isArray(result) ? result : result?.data ?? [];
        if (arr.length === 0) {
          return useDummyDataWhenEmpty
            ? { data: getDummySugarReadings(), isDummyData: true }
            : { data: [], isDummyData: false };
        }
        return { data: arr, isDummyData: false };
      } catch {
        return useDummyDataWhenEmpty
          ? { data: getDummySugarReadings(), isDummyData: true }
          : { data: [], isDummyData: false };
      }
    },
    enabled: !!user,
    staleTime: 2 * 60 * 1000, // 2 minutes - reduce refetches
    retry: shouldRetry,
  });

  // Always return a safe object - never undefined (fixes "Cannot destructure property 'data' of ... as it is undefined")
  if (query == null || typeof query !== 'object') {
    return { data: undefined, isLoading: true, isError: false, error: null, refetch: () => {}, isDummyData: false };
  }
  const rawData = query.data;
  const data = rawData?.data ?? (Array.isArray(rawData) ? rawData : undefined);
  const isDummyData = rawData?.isDummyData === true;
  return {
    data,
    isLoading: query.isLoading ?? true,
    isError: query.isError ?? false,
    error: query.error ?? null,
    refetch: query.refetch ?? (() => {}),
    isDummyData,
  };
}
